import {
  matchServedDomain,
  normaliseHost,
  parseIdentityUrlsByDomain,
  resolveIdentityUrl,
} from './identityDomain';

/**
 * Application Configuration
 * Reads from environment variables
 * Environment variables must be prefixed with REACT_APP_ to be accessible in the browser
 * Make sure .env file exists and restart dev server after modifying it
 */

// Validate critical environment variables
if (!process.env.REACT_APP_IDENTITY_URL) {
  console.error('ERROR: REACT_APP_IDENTITY_URL is not set in environment variables.');
  console.error('Please ensure .env file exists with REACT_APP_IDENTITY_URL configured.');
  console.error('After creating/updating .env file, restart the development server.');
}

export const config = {
  BASE_URL:process.env.REACT_APP_BASE_URL,
  BASE_URL_Insights: process.env.REACT_APP_BASE_URL_INSIGHTS,
  APP_URL: process.env.REACT_APP_APP_URL,
  WELLBEING_APP_URL: process.env.REACT_APP_WELLBEING_APP_URL,
  INSIGHTS_APP_URL: process.env.REACT_APP_INSIGHTS_APP_URL,
  HOME_APP_URL: process.env.REACT_APP_HOME_APP_URL,
  EI_APP_URL: process.env.REACT_APP_EI_APP_URL,
  WELBEE_EMAIL_URL: process.env.REACT_APP_WELBEE_EMAIL_URL,
  
  GOOGLE_ANALYTICS_KEY: process.env.REACT_APP_GOOGLE_ANALYTICS_KEY,
  AMPLITUDE_KEY: process.env.REACT_APP_AMPLITUDE_KEY,
  HOTJAR: {
    hjid: process.env.REACT_APP_HOTJAR_HJID,
    hjsv: process.env.REACT_APP_HOTJAR_HJSV,
  },
  
  EMAIL_API_KEY: process.env.REACT_APP_EMAIL_API_KEY,
  IDENTITY_URL: process.env.REACT_APP_IDENTITY_URL,
  /** Welbee Identity API (REST), e.g. https://welbee-idenity-api-prod-....run.app */
  IDENTITY_API_URL:
    process.env.REACT_APP_IDENTITY_API_URL || process.env.REACT_APP_API_URL,
  /** EDU-Intelligence credits & reporting API (FastAPI), e.g. https://edu-intelligence-api-....run.app */
  EI_API_URL: process.env.REACT_APP_EI_API_URL,
};

/**
 * The Identity app URL for the domain this browser is actually on.
 *
 * Prefer this over `config.IDENTITY_URL` anywhere the value is used to reach the Identity app.
 * The build-time value names a single registrable domain, and the session cookie does not cross
 * registrable domains — so on the other domain a credentialed call to it is sent without the
 * cookie and comes back 401, which reads as "signed out immediately after signing in".
 *
 * A domain with no configured entry falls back to the build-time value, leaving the original
 * domain's behaviour untouched.
 */
export function getIdentityUrl() {
  const host = typeof window === 'undefined' ? '' : window.location.hostname;

  return resolveIdentityUrl(
    host,
    parseIdentityUrlsByDomain(process.env.REACT_APP_IDENTITY_URLS_BY_DOMAIN),
    config.IDENTITY_URL
  );
}

/**
 * The sibling-app URLs for the domain this browser is actually on.
 *
 * Impersonation and the subscription views send the user to another product. Those URLs are
 * build-time constants naming a single registrable domain, so from `admin.edu-intelligence.ai`
 * they hand the user to `home.welbee.co.uk` — a different registrable domain, where the session
 * cookie cannot follow and the impersonated session is lost.
 *
 * A domain with no configured entry keeps the build-time value, so welbee.co.uk is unchanged.
 */
export function getAppUrls(
  host = typeof window === 'undefined' ? '' : window.location.hostname
) {

  let byDomain = {};
  try {
    const raw = process.env.REACT_APP_APP_URLS_BY_DOMAIN;
    const unquoted = String(raw || '').trim().replace(/^(['"])([\s\S]*)\1$/, '$2');
    const parsed = unquoted ? JSON.parse(unquoted) : {};
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) byDomain = parsed;
  } catch {
    // Malformed configuration falls back to the build-time values rather than breaking navigation.
  }

  // Normalise the configured keys: matchServedDomain returns the normalised form (lower-cased,
  // no leading dot), so a key written as ".edu-intelligence.ai" would match and then miss here.
  const normalised = Object.fromEntries(
    Object.entries(byDomain).map(([k, v]) => [normaliseHost(k).replace(/^\./, ''), v])
  );
  const matched = matchServedDomain(host, Object.keys(normalised));
  const overrides = (matched && normalised[matched]) || {};

  return {
    HOME_APP_URL: overrides.HOME_APP_URL || config.HOME_APP_URL,
    INSIGHTS_APP_URL: overrides.INSIGHTS_APP_URL || config.INSIGHTS_APP_URL,
    WELLBEING_APP_URL: overrides.WELLBEING_APP_URL || config.WELLBEING_APP_URL,
    EI_APP_URL: overrides.EI_APP_URL || config.EI_APP_URL,
  };
}
